import React, { useCallback, useEffect, useRef, useState } from "react";
import styled from "styled-components";
import { Heading2, Paragraph, Subtitle } from "@/components/typography";

import border from "../assets/images/blank_zelda_border.png";
import { useNavigate } from "react-router-dom";
import MarcButton from "@/components/buttons/MarcButton";
import ScrollMoreIndicator from "@/components/ScrollMoreIndicator";
import resumeData from "@/data/resume.json";

type Job = {
  id: number;
  dates: string;
  title: string;
  company: string;
  bullets: string[];
};

/* ── Layout containers ─────────────────────────────────────────────── */

const ResumeContainer = styled.div`
  background: black;
  color: white;
  width: 100%;
  height: 100vh;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  gap: 24px;
  box-sizing: border-box;
  position: relative;

  @media (max-width: 1199px) {
    flex-direction: column;
    gap: 12px;
    padding: 48px 16px 16px;
  }
`;

const BackColumn = styled.div`
  flex-shrink: 0;
  align-self: flex-start;
  padding-top: 40px;
  padding-left: 20px;

  @media (max-width: 1199px) {
    position: absolute;
    top: 12px;
    left: 12px;
    padding: 0;
    z-index: 10;
  }
`;

/* ── Scroll frame (Zelda-bordered window) ──────────────────────────── */

const ScrollFrame = styled.div`
  flex-shrink: 0;
  border-image: url(${border}) 8 / 32px / 0 round;
  border-width: 32px;
  border-style: inset;
  width: 600px;
  height: 80vh;
  overflow-y: auto;
  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
  }

  @media (max-width: 1199px) {
    width: min(600px, calc(100vw - 32px));
    flex: 1;
    min-height: 0;
    height: auto;
  }

  @media (max-width: 768px) {
    width: calc(100vw - 32px);
  }
`;

const ScrollFrameWrapper = styled.div`
  position: relative;
  flex-shrink: 0;

  @media (max-width: 1199px) {
    flex: 1;
    min-height: 0;
    display: flex;
    flex-direction: column;
  }
`;

const ScrollContent = styled.div`
  padding: 24px;
  display: flex;
  flex-direction: column;
`;

/** Empty spacer that gives the Star Wars crawl room to animate */
const CrawlSpacer = styled.div<{ $height: string }>`
  height: ${(props) => props.$height};
  flex-shrink: 0;
`;

const JobSpacer = styled.div`
  height: 200px;

  @media (max-width: 768px) {
    height: 100px;
  }
`;

/* ── Shared spacer (also used by Interests page) ───────────────────── */

export const Spacer = styled.div`
  padding: 64px;
`;

/* ── Component ─────────────────────────────────────────────────────── */

function Resume() {
  const [isCrawling, setIsCrawling] = useState(true);
  const scrollFrameRef = useRef<HTMLDivElement>(null);
  const animationRef = useRef<number | null>(null);
  const navigate = useNavigate();

  // ── Star Wars crawl: animate scrollTop from 0 → max over 35 s ────
  useEffect(() => {
    if (!isCrawling || !scrollFrameRef.current) return;

    const el = scrollFrameRef.current;
    const startTime = performance.now();
    const duration = 35000;

    const animate = (now: number) => {
      const total = el.scrollHeight - el.clientHeight;
      const progress = Math.min((now - startTime) / duration, 1);
      el.scrollTop = progress * total;

      if (progress < 1) {
        animationRef.current = requestAnimationFrame(animate);
      } else {
        // crawl finished naturally
        setIsCrawling(false);
      }
    };

    animationRef.current = requestAnimationFrame(animate);
    return () => {
      if (animationRef.current) cancelAnimationFrame(animationRef.current);
    };
  }, [isCrawling]);

  // ── Stop crawl & hand control to user ─────────────────────────────
  const stopCrawl = useCallback(() => {
    if (!isCrawling) return;
    setIsCrawling(false);
    if (animationRef.current) {
      cancelAnimationFrame(animationRef.current);
      animationRef.current = null;
    }
  }, [isCrawling]);

  // ── Detect wheel / touch to stop crawl ────────────────────────────
  useEffect(() => {
    const el = scrollFrameRef.current;
    if (!el || !isCrawling) return;

    const handle = () => stopCrawl();
    el.addEventListener("wheel", handle, { passive: true });
    el.addEventListener("touchstart", handle, { passive: true });
    return () => {
      el.removeEventListener("wheel", handle);
      el.removeEventListener("touchstart", handle);
    };
  }, [isCrawling, stopCrawl]);

  // ── Render ────────────────────────────────────────────────────────
  return (
    <ResumeContainer>
      <BackColumn>
        <MarcButton onClick={() => navigate("/")} label={"Back"} />
      </BackColumn>

      <ScrollFrameWrapper>
        <ScrollFrame ref={scrollFrameRef}>
          <ScrollContent>
            <CrawlSpacer $height="20vh" />

            {(resumeData.jobs as Job[]).map((job, index) => (
              <React.Fragment key={job.id}>
                <div style={{ flexDirection: "column" }}>
                  <Heading2 style={{ textAlign: "center" }}>{job.dates}</Heading2>
                  <Heading2 style={{ textAlign: "center" }}>{job.title}</Heading2>
                  <Subtitle style={{ paddingTop: 12, textAlign: "center" }}>
                    {job.company}
                  </Subtitle>
                  {job.bullets.map((bullet, bulletIndex) => (
                    <Paragraph
                      key={bulletIndex}
                      style={{ paddingTop: 36, fontSize: 20, lineHeight: "28px" }}
                    >
                      * {bullet}
                    </Paragraph>
                  ))}
                </div>
                {index < resumeData.jobs.length - 1 && <JobSpacer />}
              </React.Fragment>
            ))}

            <CrawlSpacer $height="20vh" />
          </ScrollContent>
        </ScrollFrame>
        <ScrollMoreIndicator scrollRef={scrollFrameRef} />
      </ScrollFrameWrapper>
    </ResumeContainer>
  );
}

export default Resume;
